// src/index.ts
import { isControllerSpec, isInitials } from "@forgesworn/joystick-protocol";

// src/foregather.ts
async function reviewDecision(decision, signal) {
  if (!signal) return decision;
  return new Promise((resolve, reject) => {
    const abort = () => reject(signal.reason);
    if (signal.aborted) {
      reject(signal.reason);
      return;
    }
    signal.addEventListener("abort", abort, { once: true });
    Promise.resolve(decision).then(resolve, reject).finally(() => signal.removeEventListener("abort", abort));
  });
}
async function receiveControllerInvitation({ flow, parseInvitation, review, clientOptions, signal }) {
  if (typeof parseInvitation !== "function" || typeof review !== "function") throw new Error("Controller invitation policy required");
  signal?.throwIfAborted();
  const payload = await flow.receive({ signal });
  let parsed;
  try {
    if (!(payload instanceof Uint8Array) || payload.length > 16364) throw new Error("Invalid controller invitation");
    parsed = parseInvitation(new TextDecoder("utf-8", { fatal: true }).decode(payload));
  } finally {
    payload.fill(0);
  }
  if (!parsed || typeof parsed.sessionId !== "string" || typeof parsed.ws !== "string" || parsed.version !== void 0 && parsed.version !== 2) throw new Error("Invalid controller invitation");
  const token = Object.freeze({ sessionId: parsed.sessionId, ws: parsed.ws, ...parsed.version === 2 ? { version: 2 } : {} });
  signal?.throwIfAborted();
  if (await reviewDecision(review(token), signal) !== true) return null;
  signal?.throwIfAborted();
  return startControllerClient(token, clientOptions);
}

// src/index.ts
var HAPTIC_PRESETS = {
  tap: [12],
  pulse: [30],
  thud: [40, 30, 80],
  win: [60, 40, 60, 40, 120],
  fail: [120, 60, 120]
};
var READY_OPEN = 1;
function startControllerClient(token, opts = {}) {
  if (token.version !== void 0 && token.version !== 2) throw Error("Invalid controller version");
  if (!/^[a-z0-9_-]{4,128}$/i.test(token.sessionId) || token.version === 2 && token.sessionId.length < 6) throw Error("Invalid controller session");
  const WS = opts.WebSocketImpl ?? globalThis.WebSocket;
  const setT = opts.setTimeoutImpl ?? ((h, ms) => globalThis.setTimeout(h, ms));
  const clearT = opts.clearTimeoutImpl ?? ((id) => globalThis.clearTimeout(id));
  let reconnectTimer;
  let controllerPulse;
  let resumeToken = /^[a-f0-9]{64}$/.test(opts.resumeToken ?? "") ? opts.resumeToken : void 0;
  let ws = null;
  let closed = false;
  let paired = false;
  let spec = null;
  let playerSlot = null;
  let reconnectAttempt = 0;
  let statusCb = null;
  let specCb = null;
  let welcomeCb = null;
  let hapticsEnabled = opts.hapticsEnabled !== false;
  let hapticCb = null;
  let phoneContext = null;
  let gameScoreCb = null;
  let spectatorViewCb = null;
  let feedbackCb = null;
  const resolveHapticPattern = (pattern) => {
    if (Array.isArray(pattern)) {
      const sanitised = pattern.filter((n) => typeof n === "number" && Number.isFinite(n) && n >= 0 && n <= 5e3).slice(0, 16);
      return sanitised.length > 0 ? sanitised : null;
    }
    return HAPTIC_PRESETS[pattern] ?? null;
  };
  const sendPhoneContext = () => {
    if (!isOpen() || !ws || !phoneContext) return;
    const frame = {
      type: "phone-context",
      tournamentTotal: phoneContext.tournamentTotal,
      ...isInitials(phoneContext.name) ? { name: phoneContext.name } : {}
    };
    try {
      ws.send(JSON.stringify(frame));
    } catch {
    }
  };
  const runHaptic = (pattern) => {
    const resolved = resolveHapticPattern(pattern);
    if (!resolved) return;
    try {
      hapticCb?.(resolved);
    } catch {
    }
    if (!hapticsEnabled) return;
    const nav = globalThis.navigator;
    if (!nav || typeof nav.vibrate !== "function") return;
    try {
      nav.vibrate(resolved);
    } catch {
    }
  };
  const fireStatus = (s) => {
    try {
      statusCb?.(s);
    } catch {
    }
  };
  const fireSpec = (s) => {
    try {
      specCb?.(s);
    } catch {
    }
  };
  const fireWelcome = (slot) => {
    try {
      welcomeCb?.(slot);
    } catch {
    }
  };
  const endpoint = new URL(token.ws);
  endpoint.searchParams.set("s", token.sessionId);
  endpoint.searchParams.set("r", "phone");
  endpoint.searchParams.set("recover", "1");
  endpoint.searchParams.set("heartbeat", "1");
  if (token.version === 2) endpoint.searchParams.set("v", "2");
  else endpoint.searchParams.delete("v");
  const url = endpoint.href;
  const isOpen = () => !!ws && ws.readyState === READY_OPEN;
  const handleFrame = (obj) => {
    if (obj.type === "error" && obj.code === "session-full") {
      closed = true;
      paired = false;
      spec = null;
      playerSlot = null;
      fireStatus({ kind: "full" });
      return;
    }
    if (obj.type === "welcome") {
      const w = obj;
      if (typeof w.resumeToken === "string" && /^[a-f0-9]{64}$/.test(w.resumeToken)) {
        resumeToken = w.resumeToken;
        try {
          opts.onResumeToken?.(resumeToken);
        } catch {
        }
      }
      if (typeof w.p === "number") {
        playerSlot = w.p;
        fireWelcome(w.p);
      }
      return;
    }
    if (obj.type === "peer-up" || obj.type === "host-up") {
      if (!paired) {
        paired = true;
        fireStatus({ kind: "paired" });
        sendPhoneContext();
      }
      return;
    }
    if (obj.type === "peer-down" || obj.type === "host-down") {
      spec = null;
      if (paired) {
        paired = false;
        fireStatus({ kind: "waiting" });
      }
      return;
    }
    if (obj.type === "controller-spec") {
      const incoming = obj.spec;
      if (isControllerSpec(incoming)) {
        spec = incoming;
        fireSpec(incoming);
      }
      return;
    }
    if (obj.type === "controller-feedback") {
      if (typeof obj.message === "string" && obj.message.length <= 512 && (obj.selected === void 0 || obj.selected === null || ["A", "B", "X", "Y", "L1", "L2", "R1", "R2", "start", "select", "joyL", "joyR", "dpadU", "dpadD", "dpadL", "dpadR"].includes(obj.selected)) && (obj.locked === void 0 || typeof obj.locked === "boolean") && (obj.tone === void 0 || ["neutral", "success", "error"].includes(obj.tone))) {
        try {
          feedbackCb?.(obj);
        } catch {
        }
      }
      return;
    }
    if (obj.type === "haptic") {
      const h = obj;
      if (h.pattern !== void 0) runHaptic(h.pattern);
      return;
    }
    if (obj.type === "game-score") {
      const g = obj;
      const score = typeof g.score === "number" && Number.isFinite(g.score) ? g.score : 0;
      const label = typeof g.label === "string" ? g.label : null;
      try {
        gameScoreCb?.(score, label);
      } catch {
      }
      return;
    }
    if (obj.type === "spectator-view") {
      const s = obj;
      if (typeof s.title === "string" && Array.isArray(s.leaderboard)) {
        try {
          spectatorViewCb?.(s);
        } catch {
        }
      }
      return;
    }
  };
  const connect = () => {
    if (closed) return;
    fireStatus({ kind: reconnectAttempt > 0 ? "reconnecting" : "connecting" });
    try {
      ws = new WS(url);
    } catch {
      scheduleReconnect();
      return;
    }
    const currentSocket = ws;
    ws.onopen = () => {
      if (closed || ws !== currentSocket) {
        currentSocket.close();
        return;
      }
      try {
        ws?.send(JSON.stringify({ type: "controller-join", ...resumeToken ? { resumeToken } : {} }));
      } catch {
      }
      clearInterval(controllerPulse);
      controllerPulse = setInterval(() => {
        if (ws === currentSocket && isOpen()) try {
          currentSocket.send(JSON.stringify({ type: "controller-heartbeat" }));
        } catch {
        }
      }, 500);
      reconnectAttempt = 0;
      fireStatus(paired ? { kind: "paired" } : { kind: "waiting" });
      if (paired) sendPhoneContext();
    };
    ws.onmessage = (ev) => {
      if (closed || ws !== currentSocket) return;
      const data = typeof ev.data === "string" ? ev.data : "";
      if (!data) return;
      let parsed;
      try {
        parsed = JSON.parse(data);
      } catch {
        return;
      }
      if (!parsed || typeof parsed !== "object") return;
      handleFrame(parsed);
    };
    ws.onerror = () => {
    };
    ws.onclose = () => {
      if (ws !== currentSocket) return;
      clearInterval(controllerPulse);
      if (paired) {
        paired = false;
      }
      spec = null;
      playerSlot = null;
      if (!closed) fireStatus({ kind: "reconnecting" });
      scheduleReconnect();
    };
  };
  const scheduleReconnect = () => {
    if (closed) return;
    reconnectAttempt += 1;
    const delay = Math.min(15e3, 500 * Math.pow(1.6, reconnectAttempt)) * (0.75 + Math.random() * 0.5);
    clearT(reconnectTimer);
    reconnectTimer = setT(connect, delay);
  };
  connect();
  const sendInput = (slot, value) => {
    if (closed || !paired || !isOpen() || !ws) return;
    const frame = { k: slot, v: value };
    try {
      ws.send(JSON.stringify(frame));
    } catch {
    }
  };
  const close = () => {
    if (closed) return;
    clearT(reconnectTimer);
    clearInterval(controllerPulse);
    if (isOpen()) try {
      ws?.send(JSON.stringify({ type: "controller-leave" }));
    } catch {
    }
    closed = true;
    paired = false;
    spec = null;
    playerSlot = null;
    resumeToken = void 0;
    try {
      opts.onResumeToken?.(null);
    } catch {
    }
    if (ws) try {
      ws.close();
    } catch {
    }
    fireStatus({ kind: "closed" });
  };
  return {
    get paired() {
      return paired;
    },
    get spec() {
      return spec;
    },
    get playerSlot() {
      return playerSlot;
    },
    sendInput,
    setHaptics: (enabled) => {
      hapticsEnabled = enabled;
    },
    setPhoneContext: (ctx) => {
      phoneContext = ctx;
      if (paired && ctx) sendPhoneContext();
    },
    onGameScore: (cb) => {
      gameScoreCb = cb;
    },
    close,
    onStatus: (cb) => {
      statusCb = cb;
    },
    onSpec: (cb) => {
      specCb = cb;
    },
    onWelcome: (cb) => {
      welcomeCb = cb;
    },
    onHaptic: (cb) => {
      hapticCb = cb;
    },
    onSpectatorView: (cb) => {
      spectatorViewCb = cb;
    },
    onFeedback: (cb) => {
      feedbackCb = cb;
    }
  };
}
export {
  receiveControllerInvitation,
  startControllerClient
};
//# sourceMappingURL=index.js.map